/**
 * Cart Module
 *
 * Manages cart state, renders cart drawer content,
 * and handles add-to-cart / quantity / remove interactions.
 */

import * as api from "./cart-api.js";

const STORAGE_KEY_ID = "vanyx_cart_id";
const STORAGE_KEY_COUNT = "vanyx_cart_count";

let cart = null;
let cartRestorePromise = null;
let drawerOpen = false;
let addPending = false;

// ---------------------------------------------------------------------------
// Security helpers
// ---------------------------------------------------------------------------

function escapeHtml(str) {
  if (!str) {
    return "";
  }
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ---------------------------------------------------------------------------
// GA4 e-commerce tracking
// ---------------------------------------------------------------------------

function trackEvent(eventName, params) {
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
}

function cartLineToItem(line) {
  return {
    item_id: line.variantId || line.id,
    item_name: line.title,
    item_variant: line.variantTitle || undefined,
    price: parseFloat(line.lineTotal) / line.quantity,
    quantity: line.quantity,
    currency: line.currency || "EUR",
  };
}

// ---------------------------------------------------------------------------
// State persistence
// ---------------------------------------------------------------------------

function saveCartId(id) {
  localStorage.setItem(STORAGE_KEY_ID, id);
}

function getSavedCartId() {
  return localStorage.getItem(STORAGE_KEY_ID);
}

function updateBadge() {
  const count = cart ? cart.totalQuantity : 0;
  localStorage.setItem(STORAGE_KEY_COUNT, String(count));

  const badge = document.querySelector("[data-cart-badge]");
  if (badge) {
    badge.textContent = String(count);
    badge.style.display = count > 0 ? "" : "none";
  }
}

// ---------------------------------------------------------------------------
// Cart drawer rendering
// ---------------------------------------------------------------------------

function formatPrice(amount, currency) {
  const num = parseFloat(amount);
  if (currency === "EUR") {
    return num.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " \u20AC";
  }
  return num.toFixed(2) + " " + currency;
}

function renderDrawer() {
  const itemsEl = document.querySelector("[data-cart-items]");
  const totalEl = document.querySelector("[data-cart-total]");
  const checkoutEl = document.querySelector("[data-cart-checkout]");
  const footerEl = document.querySelector("[data-cart-footer]");
  const emptyEl = document.querySelector("[data-cart-empty]");

  if (!itemsEl) {
    return;
  }

  if (!cart || cart.lines.length === 0) {
    itemsEl.innerHTML = "";
    if (emptyEl) {
      emptyEl.style.display = "";
    }
    if (footerEl) {
      footerEl.style.display = "none";
    }
    return;
  }

  if (emptyEl) {
    emptyEl.style.display = "none";
  }
  if (footerEl) {
    footerEl.style.display = "";
  }

  const lang = (window.VANYX_CONFIG || {}).lang || "de";

  itemsEl.innerHTML = cart.lines
    .map(
      (line) => `
    <div class="cart-drawer__item" data-line-id="${escapeHtml(line.id)}">
      ${line.image ? `<img src="${escapeHtml(line.image)}" alt="${escapeHtml(line.imageAlt || line.title)}" class="cart-drawer__item-image" width="64" height="64" loading="lazy" />` : '<div class="cart-drawer__item-image cart-drawer__item-image--empty"></div>'}
      <div class="cart-drawer__item-info">
        <p class="cart-drawer__item-title">${escapeHtml(line.title)}</p>
        ${line.variantTitle ? `<p class="cart-drawer__item-variant">${escapeHtml(line.variantTitle)}</p>` : ""}
      </div>
      <div class="cart-drawer__item-actions">
        <div class="cart-drawer__item-quantity">
          <button type="button" class="cart-drawer__qty-btn" data-quantity-minus aria-label="${lang === "en" ? "Decrease quantity" : "Menge verringern"}">&#8722;</button>
          <span data-quantity-value>${line.quantity}</span>
          <button type="button" class="cart-drawer__qty-btn" data-quantity-plus aria-label="${lang === "en" ? "Increase quantity" : "Menge erhöhen"}">+</button>
        </div>
        <button type="button" class="cart-drawer__item-remove" data-remove-line aria-label="${lang === "en" ? "Remove" : "Entfernen"}">&times;</button>
        <span class="cart-drawer__item-price">${formatPrice(line.lineTotal, line.currency)}</span>
      </div>
    </div>
  `,
    )
    .join("");

  if (totalEl) {
    totalEl.textContent = formatPrice(cart.totalAmount, cart.currency);
  }
  if (checkoutEl) {
    checkoutEl.href = cart.checkoutUrl;
  }
}

// ---------------------------------------------------------------------------
// Drawer open / close
// ---------------------------------------------------------------------------

function openDrawer() {
  const drawer = document.getElementById("cart-drawer");
  if (!drawer) {
    return;
  }
  drawer.setAttribute("aria-hidden", "false");
  document.body.classList.add("cart-open");
  drawerOpen = true;
  renderDrawer();

  // GA4 view_cart on drawer open (only when there's something in the cart)
  if (cart && cart.lines && cart.lines.length > 0) {
    trackEvent("view_cart", {
      currency: cart.currency || "EUR",
      value: parseFloat(cart.totalAmount),
      items: cart.lines.map(cartLineToItem),
    });
  }
}

function closeDrawer() {
  const drawer = document.getElementById("cart-drawer");
  if (!drawer) {
    return;
  }
  drawer.setAttribute("aria-hidden", "true");
  document.body.classList.remove("cart-open");
  drawerOpen = false;
}

function toggleDrawer() {
  if (drawerOpen) {
    closeDrawer();
  } else {
    openDrawer();
  }
}

// ---------------------------------------------------------------------------
// Cart actions
// ---------------------------------------------------------------------------

async function ensureCart() {
  if (cart) {
    return cart;
  }
  if (!cartRestorePromise) {
    const savedId = getSavedCartId();
    if (savedId) {
      cartRestorePromise = api.getCart(savedId).then((restored) => {
        cartRestorePromise = null;
        if (restored) {
          cart = restored;
        } else {
          localStorage.removeItem(STORAGE_KEY_ID);
          localStorage.removeItem(STORAGE_KEY_COUNT);
        }
        return cart;
      }).catch(() => {
        cartRestorePromise = null;
        localStorage.removeItem(STORAGE_KEY_ID);
        localStorage.removeItem(STORAGE_KEY_COUNT);
        return null;
      });
    }
  }
  if (cartRestorePromise) {
    await cartRestorePromise;
  }
  return cart;
}

async function addItem(variantId, quantity = 1) {
  if (addPending) {
    const lang = (window.VANYX_CONFIG || {}).lang || "de";
    showToast(lang === "en" ? "Processing\u2026" : "Wird verarbeitet\u2026");
    return;
  }
  addPending = true;
  try {
    await ensureCart();

    if (cart) {
      cart = await api.addToCart(cart.id, variantId, quantity);
    } else {
      cart = await api.createCart(variantId, quantity);
      saveCartId(cart.id);
    }

    updateBadge();
    openDrawer();
  } catch {
    // Cart might be expired — create new one
    try {
      cart = await api.createCart(variantId, quantity);
      saveCartId(cart.id);
      updateBadge();
      openDrawer();
    } catch {
      throw new Error("Failed to create cart");
    }
  } finally {
    addPending = false;
  }
}

async function updateItem(lineId, quantity) {
  if (!cart) {
    return;
  }
  try {
    if (quantity <= 0) {
      cart = await api.removeLine(cart.id, lineId);
    } else {
      cart = await api.updateLine(cart.id, lineId, quantity);
    }
    updateBadge();
    renderDrawer();
  } catch {
    // Re-render to reset UI to last known state
    renderDrawer();
  }
}

async function removeItem(lineId) {
  if (!cart) {
    return;
  }
  // Capture line data before removal for GA4
  const removedLine = cart.lines.find((l) => l.id === lineId);
  try {
    cart = await api.removeLine(cart.id, lineId);
    updateBadge();
    renderDrawer();

    // GA4 remove_from_cart
    if (removedLine) {
      trackEvent("remove_from_cart", {
        currency: removedLine.currency || "EUR",
        value: parseFloat(removedLine.lineTotal),
        items: [cartLineToItem(removedLine)],
      });
    }
  } catch {
    // Re-render to reset UI to last known state
    renderDrawer();
  }
}

// ---------------------------------------------------------------------------
// Event listeners
// ---------------------------------------------------------------------------

function initDrawerEvents() {
  // Close button
  const closeBtn = document.querySelector(".cart-drawer__close");
  if (closeBtn) {
    closeBtn.addEventListener("click", closeDrawer);
  }

  // Overlay click
  const overlay = document.querySelector(".cart-drawer__overlay");
  if (overlay) {
    overlay.addEventListener("click", closeDrawer);
  }

  // Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawerOpen) {
      closeDrawer();
    }
  });

  // Cart toggle button(s)
  document.querySelectorAll("[data-cart-toggle]").forEach((btn) => {
    btn.addEventListener("click", toggleDrawer);
  });

  // Quantity and remove buttons (delegated)
  const itemsEl = document.querySelector("[data-cart-items]");
  if (itemsEl) {
    itemsEl.addEventListener("click", (e) => {
      const item = e.target.closest("[data-line-id]");
      if (!item) {
        return;
      }
      const lineId = item.dataset.lineId;
      const line = cart?.lines.find((l) => l.id === lineId);
      if (!line) {
        return;
      }

      const confirmRemove = () => {
        const l = (window.VANYX_CONFIG || {}).lang || "de";
        const msg = l === "en"
          ? "Remove this item from your cart?"
          : "Diesen Artikel aus dem Warenkorb entfernen?";
        return confirm(msg);
      };

      if (e.target.closest("[data-quantity-minus]")) {
        if (line.quantity <= 1) {
          if (confirmRemove()) {
            removeItem(lineId);
          }
        } else {
          updateItem(lineId, line.quantity - 1);
        }
      } else if (e.target.closest("[data-quantity-plus]")) {
        updateItem(lineId, line.quantity + 1);
      } else if (e.target.closest("[data-remove-line]")) {
        if (confirmRemove()) {
          removeItem(lineId);
        }
      }
    });
  }

  // GA4 begin_checkout on checkout link click
  const checkoutEl = document.querySelector("[data-cart-checkout]");
  if (checkoutEl) {
    checkoutEl.addEventListener("click", () => {
      if (cart && cart.lines.length > 0) {
        trackEvent("begin_checkout", {
          currency: cart.currency || "EUR",
          value: parseFloat(cart.totalAmount),
          items: cart.lines.map(cartLineToItem),
        });
      }
    });
  }
}

function showToast(message) {
  let toast = document.querySelector(".cart-toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.className = "cart-toast";
    toast.innerHTML = `<svg class="cart-toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg><span></span>`;
    document.body.appendChild(toast);
  }
  toast.querySelector("span").textContent = message;
  // Force reflow before adding class
  void toast.offsetHeight;
  toast.classList.add("is-visible");

  clearTimeout(toast._hideTimer);
  toast._hideTimer = setTimeout(() => {
    toast.classList.remove("is-visible");
  }, 2500);
}

function initAddToCartButtons() {
  document.querySelectorAll("[data-add-to-cart]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const variantId = btn.dataset.variantId;
      if (!variantId) {
        return;
      }

      const lang = (window.VANYX_CONFIG || {}).lang || "de";

      // Show loading spinner
      btn.classList.add("is-loading");
      btn.disabled = true;

      const qtyInput = document.querySelector("[data-qty-input]");
      const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

      addItem(`gid://shopify/ProductVariant/${variantId}`, quantity)
        .then(() => {
          showToast(lang === "en" ? "Added to cart" : "Zum Warenkorb hinzugefügt");
          // GA4 add_to_cart
          if (cart) {
            const gid = `gid://shopify/ProductVariant/${variantId}`;
            const added = cart.lines.find((l) => l.variantId === gid);
            if (added) {
              trackEvent("add_to_cart", {
                currency: cart.currency || "EUR",
                value: parseFloat(added.lineTotal),
                items: [cartLineToItem(added)],
              });
            }
          }
        })
        .catch(() => {
          showToast(lang === "en" ? "Could not add to cart" : "Fehler beim Hinzufügen");
        })
        .finally(() => {
          // Reset quantity to 1 after adding
          if (qtyInput) {
            qtyInput.value = 1;
          }
          btn.classList.remove("is-loading");
          btn.disabled = false;
        });
    });
  });
}

function initBuyNow() {
  const btn = document.querySelector("[data-buy-now]");
  if (!btn) {
    return;
  }

  // Keep variant ID in sync
  const variants = document.querySelector("[data-variants]");
  if (variants) {
    variants.addEventListener("variantchange", (e) => {
      btn.dataset.variantId = e.detail.variantId;
      btn.disabled = !e.detail.available;
    });
  }

  btn.addEventListener("click", async () => {
    const variantId = btn.dataset.variantId;
    if (!variantId) {
      return;
    }

    const span = btn.querySelector("span");
    const originalText = span?.textContent;
    btn.disabled = true;
    if (span) {
      span.textContent = "...";
    }

    try {
      const qtyInput = document.querySelector("[data-qty-input]");
      const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      const newCart = await api.createCart(
        `gid://shopify/ProductVariant/${variantId}`,
        quantity,
      );
      window.location.href = newCart.checkoutUrl;
    } catch {
      btn.disabled = false;
      if (span) {
        span.textContent = originalText;
      }
    }
  });
}

function initVariantSync() {
  const variants = document.querySelector("[data-variants]");
  const addBtn = document.querySelector("[data-add-to-cart]");

  if (variants && addBtn) {
    variants.addEventListener("variantchange", (e) => {
      addBtn.dataset.variantId = e.detail.variantId;

      // Disable add-to-cart if variant is unavailable
      const span = addBtn.querySelector("span");
      const lang = (window.VANYX_CONFIG || {}).lang || "de";
      if (!e.detail.available) {
        addBtn.disabled = true;
        if (span) {
          span.textContent = lang === "en" ? "Sold out" : "Ausverkauft";
        }
      } else {
        addBtn.disabled = false;
        if (span) {
          span.textContent = lang === "en" ? "Add to Cart" : "In den Warenkorb";
        }
      }
    });
  }
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export async function init() {
  initDrawerEvents();
  initAddToCartButtons();
  initBuyNow();
  initVariantSync();

  // Restore cart state from localStorage
  await ensureCart();
  updateBadge();
}
